import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { type AuctionDivision } from "../../../Utils/auctionRules";
import { formatToIndianCurrencyWords2 } from "../../../Utils/helperFunctions";
import { getTeamLogo } from "./helpers";

type TeamMetaItem = {
  label: string;
  value: string;
};

type TeamSidebarProps = {
  activeDivision: AuctionDivision;
  onDivisionChange: (division: AuctionDivision) => void;
  filteredTeams: any[];
  selectedTeam: any;
  onTeamSelect: (team: any) => void;
  players: any[];
  teamMeta: TeamMetaItem[];
  children: ReactNode;
};

export const TeamSidebar = ({
  activeDivision,
  onDivisionChange,
  filteredTeams,
  selectedTeam,
  onTeamSelect,
  players,
  teamMeta,
  children,
}: TeamSidebarProps) => {
  const teamStripRef = useRef<HTMLDivElement | null>(null);
  const teamTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [teamPillStyle, setTeamPillStyle] = useState({
    width: 0,
    height: 0,
    transform: "translateX(0px)",
  });

  const getCategoryCount = (level: string) =>
    players.filter((player: any) => player.Recent_Competitive_Level__c === level).length;
  const activeTeamIndex = filteredTeams.findIndex(
    (team: any) => team?.Id === selectedTeam?.Id,
  );
  const selectedTeamLogo = getTeamLogo(selectedTeam);

  useEffect(() => {
    const updateTeamPill = () => {
      const activeRef = teamTabRefs.current[activeTeamIndex];
      if (!activeRef) return;

      const pillInset = activeRef.offsetWidth * 0.095;
      setTeamPillStyle({
        width: activeRef.offsetWidth + pillInset * 2,
        height: activeRef.offsetHeight + pillInset * 1.25,
        transform: `translateX(${activeRef.offsetLeft - pillInset}px)`,
      });
    };

    updateTeamPill();
    window.addEventListener("resize", updateTeamPill);
    return () => window.removeEventListener("resize", updateTeamPill);
  }, [activeTeamIndex, filteredTeams.length]);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "31.5%" },
        maxWidth: { xs: "100%", md: "31.5%" },
        flex: { xs: "1 1 0", md: "0 0 31.5%" },
        minWidth: 0,
        backgroundColor: "transparent",
        px: 0,
        pt: { xs: 0.7, md: "0.85vw" },
        pb: 0,
        mt: { xs: 0.8, md: 0 },
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "stretch", md: "flex-start" },
        height: "100%",
        minHeight: 0,
        overflowY: "hidden",
        overflowX: "hidden",
        pr: 0,
        boxSizing: "border-box",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          mb: { xs: 0.65, md: "0.45vw" },
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "inline-grid",
            gridTemplateColumns: "1fr 1fr",
            position: "relative",
            borderRadius: "376.621px",
            overflow: "hidden",
            border: "none",
            background:
              "var(--btn-grad, linear-gradient(90deg, #F58220 0%, #FE3B88 100%))",
            p: { xs: "3px", md: "0.16vw" },
            minWidth: { xs: 170, md: "13.5vw" },
            ml: 0,
            mr: { xs: 0, md: 0 },
            mx: "auto",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: 3, md: "0.16vw" },
              bottom: { xs: 3, md: "0.16vw" },
              left: { xs: 3, md: "0.16vw" },
              width: { xs: "calc(50% - 3px)", md: "calc(50% - 0.16vw)" },
              borderRadius: "999px",
              background: "#fff",
              transform:
                activeDivision === "men"
                  ? "translateX(0%)"
                  : "translateX(100%)",
              transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              zIndex: 1,
            }}
          />
          <Typography
            onClick={() => onDivisionChange("men")}
            sx={{
              px: { xs: 1.4, md: "0.9vw" },
              py: { xs: 0.5, md: "0.4vw" },
              fontSize: { xs: "0.55rem", md: "0.76vw" },
              fontWeight: 800,
              color: activeDivision === "men" ? "#09132b" : "#fff",
              lineHeight: 1,
              cursor: "pointer",
              textAlign: "center",
              zIndex: 2,
              userSelect: "none",
              transition: "color 220ms ease",
            }}
          >
            MEN
          </Typography>
          <Typography
            onClick={() => onDivisionChange("women")}
            sx={{
              px: { xs: 1.4, md: "0.9vw" },
              py: { xs: 0.5, md: "0.4vw" },
              fontSize: { xs: "0.55rem", md: "0.76vw" },
              fontWeight: 800,
              color:
                activeDivision === "women"
                  ? "#09132b"
                  : "rgba(255,255,255,0.85)",
              lineHeight: 1,
              cursor: "pointer",
              textAlign: "center",
              zIndex: 2,
              userSelect: "none",
              transition: "color 220ms ease",
            }}
          >
            WOMEN
          </Typography>
        </Box>
      </Box>

      <Box
        ref={teamStripRef}
        sx={{
          display: "flex",
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: "100%",
          gap: { xs: 1.2, md: "1.15vw" },
          overflowX: "auto",
          overflowY: "hidden",
          pt: { xs: 0.65, md: "0.55vw" },
          pb: { xs: 2.2, md: "1.5vw" },
          minHeight: { xs: 96, md: "6.7vw" },
          alignItems: "center",
          justifyContent: "flex-start",
          flexWrap: "nowrap",
          ml: 0,
          mr: 0,
          px: 0,
          background: "#000",
          boxSizing: "border-box",
          scrollPaddingInline: { xs: "10px", md: "1.15vw" },
          touchAction: "pan-x",
          overscrollBehaviorX: "contain",
          overscrollBehaviorY: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          sx={{
            flex: { xs: "0 0 10px", md: "0 0 1.15vw" },
            height: 1,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 6, md: "0.38vw" },
            left: 0,
            width: `${teamPillStyle.width}px`,
            height: `${teamPillStyle.height}px`,
            borderRadius: "999px 999px 14px 14px",
            background:
              "radial-gradient(circle at 50% 18%, rgba(255,255,255,0.08), rgba(255,255,255,0.01) 58%), #1B1B1B",
            boxShadow: "0 12px 24px rgba(0,0,0,0.36)",
            transform: teamPillStyle.transform,
            transition:
              "transform 300ms cubic-bezier(0.22, 1, 0.36, 1), width 300ms cubic-bezier(0.22, 1, 0.36, 1), height 300ms cubic-bezier(0.22, 1, 0.36, 1)",
            zIndex: 0,
            pointerEvents: "none",
            "&::after": {
              content: '""',
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: { xs: "166%", md: "160%" },
              bottom: { xs: -18, md: "-1.3vw" },
              height: { xs: 24, md: "1.8vw" },
              background: "#1B1B1B",
              clipPath: "polygon(26% 0%, 74% 0%, 100% 100%, 0% 100%)",
              borderRadius: 0,
              boxShadow: "0 8px 14px rgba(0,0,0,0.24)",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: { xs: "138%", md: "132%" },
              bottom: { xs: -15, md: "-1.1vw" },
              height: { xs: 20, md: "1.4vw" },
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 100%)",
              clipPath: "polygon(28% 0%, 72% 0%, 100% 100%, 0% 100%)",
              borderRadius: 0,
            },
          }}
        />
        {filteredTeams.map((item: any, index: number) => {
          const isActive = item?.Id === selectedTeam?.Id;
          const teamLogo = getTeamLogo(item);
          return (
            <Box
              component="button"
              key={item?.Id}
              ref={(element: HTMLButtonElement | null) => {
                teamTabRefs.current[index] = element;
              }}
              type="button"
              onClick={() => {
                onTeamSelect(item);
                const strip = teamStripRef.current;
                const tab = teamTabRefs.current[index];
                if (!strip || !tab) return;

                const maxScrollLeft = strip.scrollWidth - strip.clientWidth;
                const centeredScrollLeft =
                  tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
                const targetScrollLeft =
                  index === 0
                    ? 0
                    : index === filteredTeams.length - 1
                      ? maxScrollLeft
                      : centeredScrollLeft;

                strip.scrollTo({
                  left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
                  behavior: "smooth",
                });
              }}
              sx={{
                width: { xs: 72, md: "5vw" },
                height: { xs: 72, md: "5vw" },
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                p: { xs: 0.45, md: "0.11vw" },
                background:
                  "radial-gradient(circle at 50% 35%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 55%, rgba(0,0,0,0.5) 100%)",
                border: isActive
                  ? "2px solid #00ff4d"
                  : "1px solid rgba(255,255,255,0.6)",
                outline: "none",
                appearance: "none",
                font: "inherit",
                cursor: "pointer",
                position: "relative",
                zIndex: 1,
                transition:
                  "border-color 220ms ease, transform 220ms ease, opacity 220ms ease",
                opacity: isActive ? 1 : 0.86,
                transform: isActive ? "scale(1.05)" : "scale(1)",
                boxShadow: isActive
                  ? "0 0 0 2px rgba(0,255,77,0.2), 0 8px 24px rgba(0,0,0,0.55)"
                  : "0 6px 20px rgba(0,0,0,0.45)",
                }}
              >
              {teamLogo ? (
                <Box
                  component="img"
                  src={teamLogo}
                  alt={item?.Name || "Team"}
                  sx={{
                    width: "88%",
                    height: "88%",
                    maxWidth: "88%",
                    maxHeight: "88%",
                    objectFit: "contain",
                    borderRadius: "50%",
                  }}
                />
              ) : null}
            </Box>
          );
        })}
        <Box
          sx={{
            flex: { xs: "0 0 10px", md: "0 0 1.15vw" },
            height: 1,
            pointerEvents: "none",
          }}
        />
      </Box>

      <Box
        sx={{
          pl: { xs: "2.2vw", md: "1.15vw" },
          pr: { xs: "2.2vw", md: "1vw" },
          pt: 0,
          pb: { xs: "2vw", md: "0.8vw" },
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          background: "#1B1B1B",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 4,
            flex: "0 0 auto",
            height: { xs: "2vw", md: "1vw" },
            background: "#1B1B1B",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: { xs: "10px", md: "0.55vw" },
            background:
              "radial-gradient(circle at 12% 100%, rgba(252,105,58,0.14), rgba(252,105,58,0) 45%), linear-gradient(110deg, rgba(8,10,15,0.96), rgba(14,16,23,0.96) 65%, rgba(9,10,14,0.97))",
            p: { xs: "1.2vw", md: "0.62vw" },
            pr: { xs: "1.6vw", md: "0.72vw" },
            display: "flex",
            alignItems: "stretch",
            flexWrap: "nowrap",
            flexDirection: "row",
            gap: { xs: "1.4vw", md: "0.8vw" },
            position: "relative",
          }}
        >
          <Typography
            sx={{
              position: "absolute",
              top: { xs: "1vw", md: "0.5vw" },
              left: { xs: "1.4vw", md: "0.72vw" },
              maxWidth: { xs: "55%", md: "60%" },
              color: "#fff",
              fontSize: { xs: "2vw", md: "0.82vw" },
              fontWeight: 700,
              lineHeight: 1.15,
              pb: { xs: "0.7vw", md: "0.45vw" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              zIndex: 1,
            }}
          >
            {selectedTeam?.Name || "-"}
          </Typography>
          <Box
            sx={{
              flex: { xs: "0 0 58%", md: "1 1 64%" },
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent:
                activeDivision === "women" ? "space-between" : "flex-start",
            }}
          >
            <Box
              sx={{
                width: { xs: "31vw", md: "13.8vw" },
                height: { xs: "17vw", md: "7.2vw" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: { xs: "2.4vw", md: "1.15vw" },
              }}
            >
              {selectedTeamLogo ? (
                <Box
                  component="img"
                  src={selectedTeamLogo}
                  alt={selectedTeam?.Name || "Team"}
                  sx={{
                    width: { xs: "29vw", md: "11vw" },
                    height: { xs: "14vw", md: "5.7vw" },
                    maxWidth: { xs: "29vw", md: "11vw" },
                    maxHeight: { xs: "14vw", md: "5.7vw" },
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: "1vw", md: "0.48vw" },
                mt:
                  activeDivision === "women"
                    ? { xs: "auto", md: "auto" }
                    : { xs: "1vw", md: "0.34vw" },
              }}
            >
              {[
                {
                  label: "Icon Player",
                  count: getCategoryCount("Indian senior team"),
                  minPlayerCount: 1,
                  key: "icon",
                },
                {
                  label: "Senior Player",
                  count: getCategoryCount(
                    "First class, list A, BCCI Senior Men T20",
                  ),
                  minPlayerCount: 4,
                  key: "senior",
                },
                {
                  label: "Emerging Player",
                  count: getCategoryCount(
                    "Mumbai age group team (under 23 or under 19)",
                  ),
                  minPlayerCount: 6,
                  key: "emerging",
                },
                {
                  label: "Development Player",
                  count: getCategoryCount("Local club team"),
                  minPlayerCount: 5,
                  key: "development",
                },
              ]
                .filter((item) =>
                  activeDivision === "women"
                    ? item.key === "icon"
                    : true,
                )
                .reduce((rows: any[], item: any, idx: number) => {
                  const rowIdx = Math.floor(idx / 2);
                  rows[rowIdx] = rows[rowIdx] || [];
                  rows[rowIdx].push(item);
                  return rows;
                }, [])
                .map((row: any[], rowIdx: number) => (
                  <Box
                    key={rowIdx}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      gap: { xs: "1vw", md: "0.52vw" },
                    }}
                  >
                    {row.map((item) => (
                      <Box
                        key={item.label}
                        sx={{ flex: 1, minWidth: 0, display: "flex" }}
                      >
                        <Box
                          sx={{
                            px: { xs: "1.1vw", md: "0.5vw" },
                            py: { xs: "0.9vw", md: "0.34vw" },
                            minHeight: { xs: "7vw", md: "2vw" },
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            background:
                              "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0.01) 100%)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            boxSizing: "border-box",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "1.8vw", md: "0.54vw" },
                              whiteSpace: { xs: "normal", md: "nowrap" },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.label} ({item.count}/{item.minPlayerCount})
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "1vw", md: "0.48vw" },
              flex: { xs: "0 0 39%", md: "0 0 36%" },
              minWidth: 0,
              height: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                borderLeft: {
                  xs: "1px solid rgba(255, 255, 255, 0.24)",
                  md: "0.1vw solid rgba(255, 255, 255, 0.24)",
                },
                background:
                  "linear-gradient(90deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.00) 100%)",
                px: { xs: "1.4vw", md: "0.62vw" },
                py: { xs: "0.6vw", md: "0.3vw" },
              }}
            >
              {teamMeta.map((meta) => (
                <Box
                  key={meta.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: "1vw", md: "0.45vw" },
                    py: { xs: "0.45vw", md: "0.22vw" },
                    borderBottom: "1px solid rgba(255,255,255,0.12)",
                    "&:last-of-type": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#ff8c1e",
                      fontSize: { xs: "2vw", md: "0.62vw" },
                      fontWeight: 600,
                    }}
                  >
                    {meta.label}
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: { xs: "2vw", md: "0.62vw" } }}>
                    -
                  </Typography>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: { xs: "2vw", md: "0.62vw" },
                      fontWeight: 600,
                    }}
                  >
                    {meta.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.10) 100%)",
                px: { xs: "1.5vw", md: "0.78vw" },
                py: { xs: "1vw", md: "0.55vw" },
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: { xs: 220, md: "12.8vw" } }}>
                <Typography sx={{ fontSize: { xs: "3.1vw", md: "1.22vw" }, fontWeight: 600 }}>
                  {formatToIndianCurrencyWords2(selectedTeam?.Total_Fund__c)}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: { xs: "1.7vw", md: "0.5vw" },
                    mt: { xs: "0.3vw", md: "0.14vw" },
                  }}
                >
                  Team purse
                </Typography>
                <Box
                  sx={{
                    my: { xs: "1vw", md: "0.36vw" },
                    height: { xs: "0.14vw", md: "0.06vw" },
                    background: "rgba(255,255,255,0.14)",
                  }}
                />
                <Typography sx={{ fontSize: { xs: "4.5vw", md: "1.48vw" }, fontWeight: 700 }}>
                  {formatToIndianCurrencyWords2(selectedTeam?.Fund_Remaining__c)}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: { xs: "1.7vw", md: "0.5vw" },
                    mt: { xs: "0.2vw", md: "0.12vw" },
                  }}
                >
                  Fund remaining
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: "2vw", md: "0.7vw" },
            flex: "0 0 auto",
            pb: { xs: "2vw", md: "0.8vw" },
            boxSizing: "border-box",
            overflowY: "visible",
            overflowX: "hidden",
            width: "100%",
            scrollbarWidth: "thin",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
